import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Flex, Button, Text } from "@radix-ui/themes";
import PropTypes from "prop-types";
import { Trans } from "@lingui/macro";
import { useMutation } from "@tanstack/react-query";
import { recordSession } from "@api/survey";
import { useSurveyStore, usePersistentSurveyStore } from "@src/store/survey";
import Loading from "@components/Loading";

SurveySectionControls.propTypes = {
  isFetching: PropTypes.bool,
};

/**
 * Get the next survey section slug
 * @param {Array} surveySections - Array of survey sections
 * @param {String} surveySectionSlug - Current survey section slug
 * @param {Number} step - Step to move forward or backward
 */
const getNextSurveySectionSlug = (surveySections, surveySectionSlug, step) => {
  let currentSectionIndex = surveySections?.surveys?.findIndex(
    (section) => section.slug === surveySectionSlug,
  );
  return surveySections?.surveys[currentSectionIndex + step]?.slug;
};

export default function SurveySectionControls({ isFetching }) {
  const [searchParams, setSearchParams] = useSearchParams();
  let { surveySectionSlug } = useParams();
  const setCommentQuestion = useSurveyStore(
    (state) => state.setCommentQuestion,
  );
  const surveySections = useSurveyStore((state) => state.surveySections);
  const sessionUUID = useSurveyStore((state) => state.sessionUUID);
  const setCurrentSurveySection = useSurveyStore(
    (state) => state.setCurrentSurveySection,
  );
  const session = useMutation({ mutationFn: recordSession });
  const navigate = useNavigate();

  const handleNext = (step) => {
    const nextSlug = getNextSurveySectionSlug(
      surveySections,
      surveySectionSlug,
      step,
    );
    setCommentQuestion();
    if (
      searchParams.has("latitude") &&
      searchParams.has("longitude") &&
      searchParams.has("zoom")
    ) {
      navigate(
        `/section/${nextSlug}?latitude=${searchParams.get(
          "latitude",
        )}&longitude=${searchParams.get("longitude")}&zoom=${searchParams.get(
          "zoom",
        )}`,
      );
    } else {
      navigate(`/section/${nextSlug}`);
    }
  };

  const handleSubmit = () => {
    let userUUID = usePersistentSurveyStore.getState().userUUID;
    // Null survey_slug means that the survey is being submitted
    session.mutate({
      sessionUUID: sessionUUID,
      userUUID: userUUID,
    });
    setCurrentSurveySection(null);
    navigate("/thank-you");
  };

  return (
    <Flex gap="3" align="center" direction="row">
      {surveySections?.surveys?.length > 0 &&
      surveySections?.surveys[surveySections?.surveys?.length - 1].slug !==
        surveySectionSlug ? (
        <Button size="2" onClick={() => handleNext(1)}>
          <Trans id="SurveySectionControls.next">Next</Trans>
        </Button>
      ) : (
        <Button size="2" onClick={handleSubmit}>
          <Trans id="SurveySectionControls.submit">Submit</Trans>
        </Button>
      )}
      {surveySections?.surveys?.length > 0 &&
      surveySections?.surveys[0]?.slug !== surveySectionSlug ? (
        <Button variant="outline" size="2" onClick={() => handleNext(-1)}>
          <Trans id="SurveySectionControls.back">Back</Trans>
        </Button>
      ) : null}
      {isFetching && (
        <Loading>
          <Text>
            <Trans id="SurveySectionControls.loading">Loading</Trans>
          </Text>
        </Loading>
      )}
    </Flex>
  );
}
